// src/pages/TravelerDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaBell,
  FaCalendarCheck,
  FaHeart,
  FaPlane,
  FaShoppingBag,
  FaArrowRight,
  FaUserCircle,
  FaShieldAlt,
  FaMapMarkedAlt,
  FaBolt,
  FaSignOutAlt,
  FaSyncAlt,
  FaExclamationTriangle,
  FaUserCog,
} from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  dateOnly,
  fetchEvasionAlerts,
  fetchFavorites,
  fetchOrders,
  fetchReservations,
  getTravelerSession,
  TravelerCounts,
} from '../lib/travelerData';
import { useTransactionI18n } from '../lib/transactionI18n';

type RecentItem = {
  id: string;
  title: string;
  subtitle: string;
  type: 'alert' | 'reservation' | 'favorite' | 'order';
  date?: string | null;
  href: string;
};

const safeDateSort = (value?: string | null) => new Date(value || 0).getTime();

const TravelerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { tx, orderStatus } = useTransactionI18n();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [counts, setCounts] = useState<TravelerCounts>({ alerts: 0, reservations: 0, favorites: 0, orders: 0 });
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const email = user?.email || '';
  const displayName = useMemo(() => {
    const metaName = user?.user_metadata?.full_name || user?.user_metadata?.name;
    if (metaName) return metaName;
    if (email) return email.split('@')[0];
    return tx('travelerDefault');
  }, [user, email, tx]);

  const loadDashboard = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(silent);
    setError(null);

    try {
      const traveler = await getTravelerSession();
      if (!traveler) {
        navigate('/login?redirect=/mon-compte');
        return;
      }

      setUser(traveler.user);

      const [alertsRes, favsRes, reservationsRes, ordersRes] = await Promise.allSettled([
        fetchEvasionAlerts(traveler.uid, 5),
        fetchFavorites(traveler.uid, 5),
        fetchReservations(traveler.uid, traveler.email, 5),
        fetchOrders(traveler.uid, traveler.email, 5),
      ]);

      const alerts = alertsRes.status === 'fulfilled' ? alertsRes.value.data : [];
      const favs = favsRes.status === 'fulfilled' ? favsRes.value.data : [];
      const reservations = reservationsRes.status === 'fulfilled' ? reservationsRes.value.data : [];
      const orders = ordersRes.status === 'fulfilled' ? ordersRes.value.data : [];

      setCounts({
        alerts: alertsRes.status === 'fulfilled' ? alertsRes.value.count : 0,
        favorites: favsRes.status === 'fulfilled' ? favsRes.value.count : 0,
        reservations: reservationsRes.status === 'fulfilled' ? reservationsRes.value.count : 0,
        orders: ordersRes.status === 'fulfilled' ? ordersRes.value.count : 0,
      });

      const items: RecentItem[] = [
        ...alerts.map((a: any) => ({
          id: `alert-${a.id}`,
          type: 'alert' as const,
          title: `${a.origin || tx('departure')} → ${a.destination || tx('destination')}`,
          subtitle: `${tx('budget')} ${a.max_budget || tx('toWatch')} $ · ${a.status || 'active'}`,
          date: a.created_at,
          href: '/mes-alertes',
        })),
        ...reservations.map((r: any) => ({
          id: `reservation-${r.id}`,
          type: 'reservation' as const,
          title: r.deal_title || r.title || tx('reservationForfeo'),
          subtitle: r.business?.name || r.status || r.payment_status || tx('confirmed'),
          date: r.created_at || r.slot_start,
          href: '/mes-reservations',
        })),
        ...orders.map((o: any) => ({
          id: `order-${o.id || o.stripe_session_id}`,
          type: 'order' as const,
          title: `${tx('order')} ${String(o.id || o.stripe_session_id || '').slice(0, 8)}`,
          subtitle: orderStatus(o.payment_status || o.status || 'processing'),
          date: o.created_at || o.paid_at,
          href: '/mes-achats',
        })),
        ...favs.map((f: any) => ({
          id: `favorite-${f.id || f.item_id}`,
          type: 'favorite' as const,
          title: f.data?.name || f.title || f.name || 'Forfeo' + ' ' + tx('favorites'),
          subtitle: f.data?.location || f.type || tx('saved'),
          date: f.created_at,
          href: '/mes-favoris',
        })),
      ]
        .sort((a, b) => safeDateSort(b.date) - safeDateSort(a.date))
        .slice(0, 8);

      setRecentItems(items);

      const failures = [alertsRes, favsRes, reservationsRes, ordersRes].filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        console.warn('TravelerDashboard partial failures:', failures);
        setError(tx('travelerPartialError'));
      }
    } catch (err: any) {
      console.error('TravelerDashboard error:', err);
      setError(tx('travelerLoadError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const statCards = [
    { label: tx('evasionAlerts'), value: counts.alerts, icon: <FaPlane />, href: '/mes-alertes', color: 'from-sky-500 to-blue-600' },
    { label: tx('reservationsBadge'), value: counts.reservations, icon: <FaCalendarCheck />, href: '/mes-reservations', color: 'from-red-500 to-orange-500' },
    { label: tx('favorites'), value: counts.favorites, icon: <FaHeart />, href: '/mes-favoris', color: 'from-pink-500 to-rose-600' },
    { label: tx('purchasesBadge'), value: counts.orders, icon: <FaShoppingBag />, href: '/mes-achats', color: 'from-emerald-500 to-teal-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />

      <main className="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="relative overflow-hidden rounded-[2rem] bg-slate-950 text-white p-8 md:p-12 mb-8 shadow-2xl">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,.35),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(249,115,22,.25),transparent_30%)]" />
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-widest text-sky-200 mb-6">
                  <FaUserCircle /> {tx('travelerSpace')}
                </div>
                <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
                  {tx('hello')}, <span className="text-sky-300">{displayName}</span>
                </h1>
                <p className="text-slate-300 max-w-2xl text-lg leading-relaxed">
                  {tx('travelerSubtitle')}
                </p>
                {email && <p className="text-xs text-slate-400 mt-4">{tx('connectedWith')} {email}</p>}
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => loadDashboard(true)}
                  disabled={refreshing}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-5 py-3 text-sm font-bold text-white ring-1 ring-white/10 hover:bg-white/15 disabled:opacity-60"
                >
                  <FaSyncAlt className={refreshing ? 'animate-spin' : ''} /> {tx('refresh')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/gestion-compte')}
                  aria-label={tx('accountManagement')}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-blue-300/30 bg-blue-500/15 px-5 py-3 text-sm font-black text-blue-50 transition hover:-translate-y-0.5 hover:bg-blue-500/25 focus:outline-none focus:ring-2 focus:ring-blue-300 sm:w-auto"
                >
                  <FaUserCog aria-hidden="true" />
                  {tx('accountManagement')}
                </button>
                <button
                  onClick={signOut}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-slate-950 px-5 py-3 text-sm font-black hover:bg-slate-100"
                >
                  <FaSignOutAlt /> {tx('signOut')}
                </button>
              </div>
            </div>
          </section>

          {error && (
            <div className="mb-8 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-amber-800 font-semibold flex gap-3">
              <FaExclamationTriangle className="mt-1 shrink-0" /> {error}
            </div>
          )}

          {loading ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-sky-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 font-bold">{tx('loadingTraveler')}</p>
            </div>
          ) : (
            <>
              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                {statCards.map((card) => (
                  <Link key={card.label} to={card.href} className="group bg-white rounded-3xl border border-slate-100 p-6 shadow-sm hover:shadow-xl transition hover:-translate-y-1">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${card.color} text-white flex items-center justify-center text-xl mb-5 shadow-lg`}>
                      {card.icon}
                    </div>
                    <div className="text-4xl font-black text-slate-900 mb-1">{card.value}</div>
                    <div className="flex items-center justify-between text-sm font-bold text-slate-500">
                      <span>{card.label}</span>
                      <FaArrowRight className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition" />
                    </div>
                  </Link>
                ))}
              </section>

              <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8">
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div>
                      <h2 className="text-2xl font-black text-slate-900">{tx('recentActivity')}</h2>
                      <p className="text-sm text-slate-500">{tx('recentActivitySubtitle')}</p>
                    </div>
                    <FaBolt className="text-amber-400 text-2xl" />
                  </div>

                  {recentItems.length === 0 ? (
                    <div className="rounded-3xl border-2 border-dashed border-slate-200 p-10 text-center">
                      <FaMapMarkedAlt className="mx-auto text-5xl text-slate-300 mb-4" />
                      <h3 className="font-black text-slate-900 mb-2">{tx('noRecentActivity')}</h3>
                      <p className="text-slate-500 mb-6">{tx('noRecentActivitySubtitle')}</p>
                      <Link to="/evasion" className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-bold">
                        {tx('createAlert')} <FaArrowRight />
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentItems.map((item) => (
                        <Link key={item.id} to={item.href} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md transition">
                          <div>
                            <div className="font-black text-slate-900">{item.title}</div>
                            <div className="text-sm text-slate-500">{item.subtitle}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xs text-slate-400 font-bold">{dateOnly(item.date)}</div>
                            <FaArrowRight className="ml-auto mt-2 text-slate-300" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <aside className="space-y-5">
                  <Link to="/last-call" className="block rounded-3xl bg-gradient-to-br from-red-600 to-orange-500 p-6 text-white shadow-lg hover:shadow-xl transition">
                    <FaCalendarCheck className="text-3xl mb-4" />
                    <h3 className="text-xl font-black mb-2">{tx('bookLastCall')}</h3>
                    <p className="text-white/80 text-sm">{tx('bookLastCallSubtitle')}</p>
                  </Link>
                  <Link to="/boutique" className="block rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-500 p-6 text-white shadow-lg hover:shadow-xl transition">
                    <FaShoppingBag className="text-3xl mb-4" />
                    <h3 className="text-xl font-black mb-2">{tx('exploreShopCard')}</h3>
                    <p className="text-white/80 text-sm">{tx('exploreShopCardSubtitle')}</p>
                  </Link>
                  <div className="rounded-3xl bg-white border border-slate-100 p-6 shadow-sm">
                    <FaShieldAlt className="text-sky-600 text-2xl mb-3" />
                    <h3 className="font-black text-slate-900 mb-2">{tx('privateData')}</h3>
                    <p className="text-sm text-slate-500">{tx('privateDataSubtitle')}</p>
                  </div>
                </aside>
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TravelerDashboard;
